import type { AttemptResultView, ApiResult } from '../attempt-engine/api.ts';
import { apiFetch } from '@/lib/api-client';

export interface AttemptSummary {
  attempt_id: string;
  test_id: string;
  test_title: string;
  track: string;
  level: string;
  user_id: string | null;
  candidate_name: string;
  candidate_email: string;
  score: number;
  passed: boolean;
  status: string;
  completed_at: string | null;
  duration_seconds: number | null;
  is_embed_attempt: boolean;
  /**
   * J-X22 — ISO timestamp when the resit lockout lifts. Present only when the
   * attempt belongs to a test with `resit_lockout != 'none'` and a
   * `resit_lockouts` row exists for this candidate+test. Null otherwise.
   */
  retake_available_at?: string | null;
}

export interface AttemptListPage {
  items: AttemptSummary[];
  next_cursor: string | null;
}

export interface BenchmarkComparison {
  attempt_id: string;
  test_id: string;
  user_score: number;
  percentile: number | null;
  sample_size: number;
}

export type AttemptResultResponse = AttemptResultView;

export async function listMyAttempts(
  baseUrl: string,
  page: { limit?: number; cursor?: string } = {},
): Promise<ApiResult<AttemptListPage>> {
  const params = new URLSearchParams();
  params.set('limit', String(page.limit ?? 25));
  if (page.cursor) params.set('cursor', page.cursor);

  const res = await apiFetch(`${baseUrl}/api/me/attempts?${params.toString()}`, {
    credentials: 'include',
  });
  try {
    return (await res.json()) as ApiResult<AttemptListPage>;
  } catch {
    // If the response is not valid JSON (e.g. HTML 404), return a synthetic error envelope.
    // This ensures the FE never hits the generic catch branch for well-formed errors.
    return {
      ok: false,
      error: {
        code: 'network',
        message: `server returned ${res.status} ${res.statusText}`,
      },
    } as ApiResult<AttemptListPage>;
  }
}

export async function getAttemptResult(
  baseUrl: string,
  attemptId: string,
): Promise<ApiResult<AttemptResultResponse>> {
  const res = await apiFetch(`${baseUrl}/api/attempts/${attemptId}/result`, {
    credentials: 'include',
  });
  try {
    return (await res.json()) as ApiResult<AttemptResultResponse>;
  } catch {
    return {
      ok: false,
      error: {
        code: 'network',
        message: `server returned ${res.status} ${res.statusText}`,
      },
    } as ApiResult<AttemptResultResponse>;
  }
}

export async function getBenchmarkComparison(
  baseUrl: string,
  attemptId: string,
): Promise<ApiResult<BenchmarkComparison>> {
  const res = await apiFetch(`${baseUrl}/api/attempts/${attemptId}/benchmark`, {
    credentials: 'include',
  });
  try {
    return (await res.json()) as ApiResult<BenchmarkComparison>;
  } catch {
    return {
      ok: false,
      error: {
        code: 'network',
        message: `server returned ${res.status} ${res.statusText}`,
      },
    } as ApiResult<BenchmarkComparison>;
  }
}
